/**
 * MarkOfflineSignedModal — action admin pour marquer un contrat comme
 * signé hors ligne (papier signé / email renvoyé). Passe 76.
 *
 * Requiert un PDF signé uploadé + date de signature effective + note.
 * Utilise `POST /api/contracts/{contract_id}/mark-offline-signed` qui
 * fait passer le contrat par le MÊME flux que la signature électronique
 * (email candidat, Academy, hired, Code RD via Passe C v2).
 */
import { useRef, useState } from "react";
import { AlertTriangle, FileSignature, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";


function todayIso() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}


export function MarkOfflineSignedModal({ open, onClose, contract, candidate, onSuccess }) {
    const [pdfFile, setPdfFile] = useState(null);
    const [signedAt, setSignedAt] = useState(todayIso());
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);

    if (!open || !contract) return null;

    function reset() {
        setPdfFile(null);
        setSignedAt(todayIso());
        setNote("");
        setBusy(false);
    }

    function handleClose() {
        if (busy) return;
        reset();
        onClose?.();
    }

    async function submit() {
        if (!pdfFile) {
            toast.error("PDF signé obligatoire — sélectionnez le fichier scanné ou reçu par email");
            return;
        }
        if (pdfFile.type !== "application/pdf" && !pdfFile.name?.toLowerCase().endsWith(".pdf")) {
            toast.error("Fichier PDF attendu (application/pdf)");
            return;
        }
        if (pdfFile.size > 15 * 1024 * 1024) {
            toast.error(`PDF trop lourd (${(pdfFile.size / 1024 / 1024).toFixed(1)} Mo > 15 Mo)`);
            return;
        }

        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("pdf", pdfFile);
            fd.append("signed_at", signedAt);
            if (note.trim()) fd.append("note", note.trim());
            const { data } = await api.post(
                `/contracts/${contract.id}/mark-offline-signed`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            toast.success(
                `Contrat marqué signé hors ligne · SHA-256 ${data.sha256.slice(0, 12)}… · Code RD attribué automatiquement`,
                { duration: 6000 },
            );
            reset();
            onSuccess?.(data);
            onClose?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Impossible de marquer signé hors ligne");
        } finally {
            setBusy(false);
        }
    }

    const candidateName = `${candidate?.first_name || ""} ${candidate?.last_name || ""}`.trim();

    return (
        <div
            className="fixed inset-0 z-[80] bg-shariti-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={handleClose}
            data-testid="mark-offline-signed-modal"
        >
            <div
                className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-shariti-border flex items-start gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0">
                        <FileSignature className="w-5 h-5 text-emerald-700" />
                    </div>
                    <div className="flex-1">
                        <h2 className="text-lg font-black text-shariti-black">Marquer signé hors ligne</h2>
                        <p className="text-sm text-slate-500 mt-1">
                            {candidateName ? <><b>{candidateName}</b> — </> : null}
                            le candidat a signé le contrat sur papier ou l'a renvoyé par email. Uploadez le PDF signé
                            pour finaliser exactement comme une signature électronique.
                        </p>
                    </div>
                </div>

                <div className="p-6 space-y-5">
                    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex gap-2">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <div>
                            Ce chemin déclenche <b>exactement le même flux</b> que la signature électronique :
                            email au candidat avec PDF joint, invitation Shariti Academy, bascule en « embauché »,
                            attribution du <b>Code RD</b>. Aucun chemin parallèle.
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-black uppercase tracking-wider text-slate-600 mb-2 block">
                            PDF signé <span className="text-red-600">*</span>
                        </label>
                        <div
                            onClick={() => inputRef.current?.click()}
                            className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition ${
                                pdfFile ? "border-emerald-400 bg-emerald-50" : "border-slate-300 hover:border-shariti-orange hover:bg-shariti-cream"
                            }`}
                            data-testid="mark-offline-pdf-dropzone"
                        >
                            <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                            {pdfFile ? (
                                <div>
                                    <div className="text-sm font-bold text-emerald-800">{pdfFile.name}</div>
                                    <div className="text-xs text-emerald-700 mt-0.5">
                                        {(pdfFile.size / 1024).toFixed(1)} Ko — cliquez pour remplacer
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className="text-sm font-bold text-slate-700">Cliquez ou déposez le PDF signé</div>
                                    <div className="text-xs text-slate-500 mt-0.5">application/pdf · max 15 Mo</div>
                                </div>
                            )}
                            <input
                                ref={inputRef}
                                type="file"
                                accept="application/pdf,.pdf"
                                className="hidden"
                                onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                                data-testid="mark-offline-pdf-input"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-black uppercase tracking-wider text-slate-600 mb-2 block">
                                Date de signature effective
                            </label>
                            <input
                                type="date"
                                value={signedAt}
                                onChange={(e) => setSignedAt(e.target.value)}
                                className="w-full border border-shariti-border rounded-lg px-3 py-2 text-sm font-mono"
                                data-testid="mark-offline-signed-at"
                                max={todayIso()}
                            />
                            <div className="text-[10px] text-slate-500 mt-1 italic">
                                Défaut = aujourd'hui · modifiable si le candidat a signé plus tôt
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-black uppercase tracking-wider text-slate-600 mb-2 block">
                                Note libre (optionnel)
                            </label>
                            <input
                                type="text"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="ex : reçu par email le 01/08"
                                className="w-full border border-shariti-border rounded-lg px-3 py-2 text-sm"
                                data-testid="mark-offline-note"
                            />
                        </div>
                    </div>
                </div>

                <div className="p-6 border-t border-shariti-border flex items-center justify-end gap-2">
                    <button
                        onClick={handleClose}
                        disabled={busy}
                        className="px-4 py-2 rounded-full border border-shariti-border bg-white text-sm font-bold hover:bg-slate-50 disabled:opacity-50"
                        data-testid="mark-offline-cancel"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={submit}
                        disabled={busy || !pdfFile}
                        className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-black uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
                        data-testid="mark-offline-submit"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />}
                        Marquer signé et attribuer Code RD
                    </button>
                </div>
            </div>
        </div>
    );
}
